/* Inline disclosures; gently reveal newly opened categories without replacing the menu. */
if (!customElements.get('sf-visual-menu')) {
  customElements.define('sf-visual-menu', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) {
        document.addEventListener('shopify:block:select', this.onEditorSelect);
        this.observeSlider();
        this.observeDrawer();
        return;
      }
      this.initialized = true;
      this.setAttribute('data-sf-quick-add-ready', '');
      this.initRoutineButtons();
      const cancelCategoryReveal = () => { this.pendingCategoryReveal = null; };
      this.addEventListener('wheel', cancelCategoryReveal, { passive: true });
      this.addEventListener('touchmove', cancelCategoryReveal, { passive: true });
      this.querySelectorAll('[data-sf-need]').forEach((need) => {
        need.addEventListener('toggle', () => {
          if (need.sfAnimation) return; // Closing keeps native details open until the animation finishes.
          need.querySelector('summary').setAttribute('aria-expanded', String(need.open));
        });
      });
      this.addEventListener('click', (event) => {
        cancelCategoryReveal();
        const quickAdd = event.target.closest('[data-sf-quick-add], [data-sf-routine-add]');
        if (quickAdd && this.contains(quickAdd)) {
          event.preventDefault();
          this.quickAdd(quickAdd);
          return;
        }
        const needsTab = event.target.closest('[data-sf-needs-tab]');
        if (needsTab && this.contains(needsTab)) {
          this.activateNeedsTab(needsTab);
          return;
        }
        const needSummary = event.target.closest('.sf-menu__need-summary');
        if (needSummary && this.contains(needSummary)) {
          event.preventDefault(); // Native summary still generates clicks for Enter and Space.
          const need = needSummary.closest('[data-sf-need]');
          const expanded = needSummary.getAttribute('aria-expanded') !== 'true';
          if (expanded) this.closeOtherNeeds(need);
          this.setNeedExpanded(need, expanded);
          return;
        }
        const sliderButton = event.target.closest('[data-sf-slider-step]');
        if (sliderButton && this.contains(sliderButton)) {
          const slider = this.querySelector('[data-sf-menu-slider]');
          const itemWidth = slider?.querySelector('li')?.getBoundingClientRect().width || 118;
          const gap = slider ? parseFloat(getComputedStyle(slider).columnGap) || 0 : 0;
          slider?.scrollBy({ left: Number(sliderButton.dataset.sfSliderStep) * (itemWidth + gap) * 2, behavior: this.reducedMotion() ? 'auto' : 'smooth' });
          return;
        }
        const button = event.target.closest('[data-sf-menu-toggle]');
        if (!button || !this.contains(button)) return;
        this.setExpanded(button, button.getAttribute('aria-expanded') !== 'true', button.classList.contains('sf-menu__toggle'));
      });
      this.addEventListener('keydown', (event) => {
        cancelCategoryReveal();
        const tab = event.target.closest('[data-sf-needs-tab]');
        if (!tab || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        const tabs = Array.from(tab.closest('[role="tablist"]').querySelectorAll('[role="tab"]'));
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 :
          (tabs.indexOf(tab) + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
        event.preventDefault();
        this.activateNeedsTab(tabs[next]);
        tabs[next].focus({ preventScroll: true });
      });
      this.addEventListener('keyup', (event) => {
        if (event.key !== 'Escape') return;
        const need = event.target.closest('[data-sf-need][open]');
        if (need) {
          event.stopPropagation();
          this.setNeedExpanded(need, false);
          need.querySelector('summary').focus();
          return;
        }
        const category = event.target.closest('.sf-menu__category');
        const button = category?.querySelector('[data-sf-menu-toggle][aria-expanded="true"]');
        if (!button) return; // Next Escape closes the native drawer.
        event.stopPropagation();
        this.setExpanded(button, false);
        button.focus();
      });
      this.onEditorSelect = (event) => {
        if (!this.contains(event.target)) return;
        const category = event.target.closest('.sf-menu__category');
        const button = category?.querySelector('[data-sf-menu-toggle]');
        if (button) this.setExpanded(button, true);
        const need = event.target.closest('[data-sf-need]');
        if (need) {
          const group = need.closest('[data-sf-needs-group]').dataset.sfNeedsGroup;
          this.activateNeedsTab(this.querySelector(`[data-sf-needs-tab="${group}"]`));
          this.closeOtherNeeds(need);
          this.setNeedExpanded(need, true, true);
        }
      };
      document.addEventListener('shopify:block:select', this.onEditorSelect);
      this.slider = this.querySelector('[data-sf-menu-slider]');
      this.slider?.addEventListener('scroll', () => this.updateSlider(), { passive: true });
      this.observeSlider();
      this.drawer = this.closest('.sf-visual-drawer');
      this.mainDetails = this.closest('header-drawer')?.querySelector('details');
      this.onCampaignScrollIntent = (event) => {
        if (event.type === 'keydown' && !['ArrowDown', 'ArrowUp', 'PageDown', 'PageUp', 'Home', 'End', ' '].includes(event.key)) return;
        if (event.type === 'keydown' && event.target.closest('input, textarea, select, [contenteditable="true"]')) return;
        if (this.mainDetails?.open || document.querySelector('cart-drawer.active, details-modal details[open]')) return;
        this.restoreCampaign();
      };
      this.onDrawerViewport = () => {
        cancelAnimationFrame(this.drawerFrame);
        this.drawerFrame = requestAnimationFrame(() => {
          this.syncCampaignHold();
          this.updateTrustAnchor();
          if (!this.mainDetails?.open || !this.drawer) return;
          // Measure the stationary header edge, not the drawer's entrance transform.
          const top = this.closest('header-drawer.sf-trust-menu') && window.matchMedia('(max-width: 989px)').matches
            ? Math.max(0, document.querySelector('.shopify-section-group-header-group .sf-bar')?.getBoundingClientRect().bottom || 0) + this.closest('.header-wrapper').getBoundingClientRect().height
            : this.drawer.getBoundingClientRect().top;
          this.drawer.style.setProperty('--sf-drawer-top', `${Math.max(0, top)}px`);
        });
      };
      this.observeDrawer();
    }
    disconnectedCallback() {
      this.cartReturnObserver?.disconnect();
      this.pendingCategoryReveal = null;
      document.removeEventListener('shopify:block:select', this.onEditorSelect);
      this.resizeObserver?.disconnect();
      this.mainDetails?.removeEventListener('toggle', this.onDrawerViewport);
      window.removeEventListener('scroll', this.onDrawerViewport);
      window.removeEventListener('resize', this.onDrawerViewport);
      window.visualViewport?.removeEventListener('resize', this.onDrawerViewport);
      window.removeEventListener('wheel', this.onCampaignScrollIntent);
      window.removeEventListener('touchmove', this.onCampaignScrollIntent);
      window.removeEventListener('keydown', this.onCampaignScrollIntent);
      this.restoreCampaign(true);
      cancelAnimationFrame(this.drawerFrame);
    }
    routineProducts(need) {
      if (!need) return [];
      const rows = Array.from(need.querySelectorAll('.sf-menu__need-item'));
      const products = rows.map((row) => row.querySelector('[data-sf-quick-add]'));
      // Never silently omit an item requiring variant selection or a selling plan.
      if (products.length < 2 || products.some((item) => !item || !/^\d+$/.test(item.dataset.variantId))) return [];
      if (new Set(products.map((item) => item.dataset.variantId)).size !== products.length) return [];
      return products;
    }
    initRoutineButtons() {
      this.querySelectorAll('[data-sf-need]').forEach((need) => {
        const footer = need.querySelector('[data-sf-routine]');
        if (!footer) return;
        const products = this.routineProducts(need);
        if (!products.length || products.some((item) => !/^\d+$/.test(item.dataset.unitPrice))) return;
        const total = products.reduce((sum, item) => sum + Number(item.dataset.unitPrice), 0);
        if (!Number.isSafeInteger(total)) return;
        try {
          footer.querySelector('[data-sf-routine-total]').textContent = new Intl.NumberFormat(document.documentElement.lang || 'de', {
            style: 'currency', currency: footer.dataset.currency
          }).format(total / 100);
        } catch (error) { return; }
        footer.querySelector('[data-sf-routine-count]').textContent = `${products.length} Produkte · je 1 Stück`;
        footer.hidden = false;
      });
    }
    async quickAdd(button) {
      // Serialize requests and reject double clicks while the first one is pending.
      if (this.quickAddPending || button.disabled || button.dataset.state === 'added') return;
      const routine = 'sfRoutineAdd' in button.dataset;
      const need = button.closest('[data-sf-need]');
      const products = routine ? this.routineProducts(need) : [button];
      if (!products.length || products.some((item) => !/^\d+$/.test(item.dataset.variantId))) return;
      const properties = { _sf_herkunft: 'Dein Bedürfnis', _sf_hinzufuegen: routine ? 'Routine' : 'Einzeln' };
      if (need?.dataset?.sfNeedTitle) properties._sf_beduerfnis = need.dataset.sfNeedTitle;
      const items = products.map((item) => ({ id: Number(item.dataset.variantId), quantity: 1, properties }));
      this.quickAddPending = true;
      const buttons = Array.from(this.querySelectorAll('[data-sf-quick-add], [data-sf-routine-add]'));
      const previousDisabled = new Map(buttons.map((item) => [item, item.disabled && item.dataset.state !== 'added']));
      const message = button.closest(routine ? '[data-sf-routine]' : '.sf-menu__need-item').querySelector('[data-sf-quick-message]');
      const originalLabel = button.getAttribute('aria-label');
      clearTimeout(button.sfAddedTimer);
      buttons.forEach((item) => { item.disabled = true; });
      button.setAttribute('aria-busy', 'true');
      delete button.dataset.state;
      message.textContent = '';
      message.classList.add('visually-hidden');
      let added = false;
      try {
        const root = window.Shopify?.routes?.root || '/';
        const endpoint = window.routes?.cart_add_url || `${root}cart/add.js`;
        const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        const sections = cart?.getSectionsToRender?.().map((section) => section.id) || ['cart-icon-bubble'];
        // Request the complete native cart, not only its counter. An empty drawer
        // needs its warnings, product rows and footer replaced together.
        const response = await fetch(endpoint, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items,
            sections,
            sections_url: window.location.pathname
          })
        });
        const data = await response.json();
        if (!response.ok || data.status) {
          message.textContent = typeof data.description === 'string' ? data.description : 'Gerade nicht verfügbar. Bitte öffne die Produktseite.';
          if (routine) message.textContent += ' Bitte prüfe den Warenkorb vor einem erneuten Hinzufügen.';
          message.classList.remove('visually-hidden');
          return;
        }
        added = true;
        button.dataset.state = 'added';
        button.setAttribute('aria-label', routine ? 'Pflegeroutine hinzugefügt' : `${button.dataset.productTitle} hinzugefügt`);
        message.textContent = routine ? `${products.length} Produkte wurden dem Warenkorb hinzugefügt.` : `${button.dataset.productTitle} wurde dem Warenkorb hinzugefügt.`;
        // A section-refresh failure must never turn a successful add into a retry prompt.
        try {
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'sf-menu-quick-add', productVariantId: routine ? undefined : button.dataset.variantId, cartData: data });
          }
        } catch (error) {
          console.warn('SF menu: product added; cart display refresh unavailable.', error);
        }
        // Render through the theme's existing cart API, as its product form does.
        // A display failure must never submit a second cart/add request.
        try {
          await this.showAddedCart(data, cart, sections, root, button);
        } catch (error) {
          console.warn('SF menu: product added; loading the full cart after a display error.', error);
          window.location.assign(window.routes?.cart_url || `${root}cart`);
        }
      } catch (error) {
        // An interrupted response does not prove the server rejected the add. Do not retry automatically.
        message.textContent = 'Bitte prüfe den Warenkorb, bevor du erneut hinzufügst. Die Verbindung wurde unterbrochen.';
        message.classList.remove('visually-hidden');
      } finally {
        button.removeAttribute('aria-busy');
        this.quickAddPending = false;
        buttons.forEach((item) => { item.disabled = previousDisabled.get(item) || item.dataset.state === 'added'; });
        if (added) {
          // Brief cooldown also prevents accidental repeated taps after a fast response.
          button.disabled = true;
          button.sfAddedTimer = setTimeout(() => {
            button.disabled = false;
            delete button.dataset.state;
            button.setAttribute('aria-label', originalLabel);
          }, 1600);
        }
      }
    }
    async showAddedCart(data, cart, sections, root, sourceButton) {
      const cartTrigger = document.getElementById('cart-icon-bubble');
      if (typeof cart?.renderContents === 'function') {
        let renderedSections = data.sections;
        const hasSections = () => sections.every((id) => typeof renderedSections?.[id] === 'string' && renderedSections[id].trim());
        if (!hasSections()) {
          // Shopify can accept the item while returning a null section. Refresh
          // only the presentation; never repeat the successful add.
          const response = await fetch(`${root}cart?sections=${encodeURIComponent(sections.join(','))}`, {
            credentials: 'same-origin', headers: { Accept: 'application/json' }
          });
          if (!response.ok) throw new Error('Cart section refresh failed');
          renderedSections = await response.json();
          if (!hasSections()) throw new Error('Cart sections unavailable');
        }
        const returnTarget = this.keepMenuBehindCart(cart, sourceButton);
        if (!returnTarget) this.closeMenuForCart();
        cart.setActiveElement?.(returnTarget || cartTrigger);
        cart.classList.remove('is-empty');
        // The multi-item Ajax response nests key/id; notifications expect them
        // at the top level, whereas drawers primarily consume sections.
        cart.renderContents({ ...data.items?.[0], ...data, sections: renderedSections });
        return;
      }
      // Keep header-trigger integrations working if a cart app replaces the
      // native renderer. Do not create a second drawer or call private app APIs.
      const html = data.sections?.['cart-icon-bubble'];
      if (cartTrigger && html) {
        const section = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
        if (section) cartTrigger.innerHTML = section.innerHTML;
      }
      this.closeMenuForCart();
      if (typeof cartTrigger?.click !== 'function') throw new Error('Cart trigger unavailable');
      cartTrigger.click();
    }
    keepMenuBehindCart(cart, sourceButton) {
      const header = this.closest('header-drawer');
      const details = header?.querySelector('details');
      // Only the native drawer has the verified stacking/active lifecycle.
      if (cart?.tagName !== 'CART-DRAWER' || !details?.open) return null;
      const summary = details.querySelector('summary');
      const viewport = header.querySelector('.menu-drawer__navigation-container');
      const scrollTop = viewport?.scrollTop;
      const scrollLeft = viewport?.scrollLeft;
      const returnTarget = { focus: () => {
        const target = sourceButton?.isConnected && !sourceButton.disabled ? sourceButton : summary;
        target?.focus({ preventScroll: true });
      } };
      this.cartReturnObserver?.disconnect();
      // Transfer focus, not menu state or its scroll lock, to the foreground cart.
      removeTrapFocus();
      let opened = cart.classList.contains('active');
      this.cartReturnObserver = new MutationObserver(() => {
        if (cart.classList.contains('active')) { opened = true; return; }
        if (!opened) return;
        this.cartReturnObserver.disconnect();
        if (!details.open || !this.isConnected) return;
        trapFocus(details, returnTarget);
        if (viewport) { viewport.scrollTop = scrollTop; viewport.scrollLeft = scrollLeft; }
      });
      this.cartReturnObserver.observe(cart, { attributes: true, attributeFilter: ['class'] });
      return returnTarget;
    }
    closeMenuForCart() {
      const headerDrawer = this.closest('header-drawer');
      const details = headerDrawer?.querySelector('details');
      const summary = details?.querySelector('summary');
      if (!details?.open || !summary) return;
      // Release the menu's focus trap and mobile scroll lock before the cart
      // takes over. Calling the normal close method preserves its animation.
      headerDrawer.closeMenuDrawer(new Event('sf-menu-quick-add'), summary);
      summary.setAttribute('aria-expanded', 'false');
    }
    syncCampaignHold() {
      if (!this.closest('header-drawer.sf-trust-menu')) return;
      if (!window.matchMedia('(max-width: 989px)').matches) {
        this.restoreCampaign(true);
        return;
      }
      if (!this.mainDetails?.open) return;
      const banner = document.querySelector('.shopify-section-group-header-group sf-campaign-countdown-v10:not([hidden])');
      const section = banner?.closest('.shopify-section');
      if (!section || (!this.campaignHold && section.getBoundingClientRect().bottom <= 0)) return;
      // Collapse behind the open menu, not during closing: the original header
      // then returns to exactly the same position when its fixed overlay ends.
      if (!this.campaignHold) this.campaignHold = { section, banner, inert: section.inert };
      clearTimeout(this.campaignRestoreTimer);
      section.style.setProperty('--sf-campaign-height', `${banner.getBoundingClientRect().height}px`);
      section.classList.remove('sf-menu-campaign-restoring');
      section.classList.add('sf-menu-campaign-held');
      section.inert = true;
    }
    restoreCampaign(immediate = false) {
      const hold = this.campaignHold;
      if (!hold) return;
      const { section, banner } = hold;
      const finish = () => {
        section.classList.remove('sf-menu-campaign-held', 'sf-menu-campaign-restoring');
        section.style.removeProperty('--sf-campaign-height');
        section.inert = hold.inert;
        this.campaignHold = null;
      };
      if (immediate || banner.hidden || this.reducedMotion()) {
        clearTimeout(this.campaignRestoreTimer);
        finish();
        return;
      }
      if (section.classList.contains('sf-menu-campaign-restoring')) return;
      section.style.setProperty('--sf-campaign-height', `${banner.getBoundingClientRect().height}px`);
      section.classList.add('sf-menu-campaign-restoring');
      section.classList.remove('sf-menu-campaign-held');
      this.campaignRestoreTimer = setTimeout(finish, 320);
    }
    updateTrustAnchor() {
      if (!this.closest('header-drawer.sf-trust-menu')) return;
      const header = this.closest('.section-header');
      const wrapper = header?.querySelector('.header-wrapper');
      if (!header || !wrapper) return;
      const trust = document.querySelector('.shopify-section-group-header-group .sf-bar');
      const bottom = Math.max(0, trust?.getBoundingClientRect().bottom || 0);
      header.style.setProperty('--sf-trust-bottom', `${bottom}px`);
      header.style.setProperty('--sf-original-header-height', `${wrapper.getBoundingClientRect().height}px`);
      if (!this.mainDetails?.open) {
        header.style.setProperty('--sf-menu-origin-top', `${Math.max(bottom, wrapper.getBoundingClientRect().top || 0)}px`);
      }
    }
    observeDrawer() {
      this.mainDetails?.addEventListener('toggle', this.onDrawerViewport);
      window.addEventListener('scroll', this.onDrawerViewport, { passive: true });
      window.addEventListener('resize', this.onDrawerViewport, { passive: true });
      window.visualViewport?.addEventListener('resize', this.onDrawerViewport, { passive: true });
      window.addEventListener('wheel', this.onCampaignScrollIntent, { passive: true });
      window.addEventListener('touchmove', this.onCampaignScrollIntent, { passive: true });
      window.addEventListener('keydown', this.onCampaignScrollIntent);
      this.onDrawerViewport();
    }
    activateNeedsTab(tab) {
      if (!tab || tab.getAttribute('aria-selected') === 'true') return;
      const needs = tab.closest('.sf-menu__needs');
      const panel = needs.querySelector('.sf-menu__panel');
      const viewport = this.closest('.menu-drawer__navigation-container');
      const scrollTop = viewport?.scrollTop;
      if (panel && viewport) {
        // Removing an open product list can shorten the drawer below its current
        // scroll offset. Reserve only the space needed to keep the tabs in place,
        // not the whole previous list. New content can grow into this space.
        const remaining = Math.max(0, viewport.scrollHeight - viewport.clientHeight - scrollTop);
        panel.style.minHeight = `${Math.max(0, Math.ceil(panel.getBoundingClientRect().height - remaining))}px`;
      }
      needs.querySelectorAll('[data-sf-needs-tab]').forEach((item) => {
        const active = item === tab;
        item.setAttribute('aria-selected', String(active));
        item.tabIndex = active ? 0 : -1;
      });
      needs.querySelectorAll('[data-sf-needs-group]').forEach((panel) => {
        panel.hidden = panel.dataset.sfNeedsGroup !== tab.dataset.sfNeedsTab;
      });
      needs.querySelectorAll('[data-sf-needs-group]').forEach((panel) => {
        if (panel.hidden) panel.querySelectorAll('[data-sf-need][open]').forEach((need) => { this.setNeedExpanded(need, false, true); });
      });
      if (viewport) viewport.scrollTop = scrollTop;
    }
    closeOtherNeeds(current) {
      current.closest('.sf-menu__needs').querySelectorAll('[data-sf-need][open]').forEach((need) => {
        if (need !== current) this.setNeedExpanded(need, false);
      });
    }
    setNeedExpanded(need, expanded, immediate = false) {
      if (!need || (!need.sfAnimation && need.open === expanded)) return;
      if (!immediate && need.sfAnimation && need.sfExpanded === expanded) return;
      const startHeight = need.getBoundingClientRect().height;
      need.sfAnimation?.cancel();
      need.sfAnimation = null;
      need.sfExpanded = expanded;
      const summary = need.querySelector('summary');
      const content = need.querySelector('.sf-menu__need-content');
      summary.setAttribute('aria-expanded', String(expanded));
      content.inert = !expanded;
      if (immediate || this.reducedMotion() || !need.animate) {
        need.open = expanded;
        return;
      }
      // Measure the real collapsed/expanded size, then keep content rendered
      // while it closes. No transforms, scaled images or fixed heights remain.
      need.open = expanded;
      const endHeight = need.getBoundingClientRect().height;
      need.open = true;
      const animation = need.animate([
        { height: `${startHeight}px`, overflow: 'hidden' },
        { height: `${endHeight}px`, overflow: 'hidden' }
      ], { duration: 220, easing: 'cubic-bezier(.2,.7,.3,1)' });
      need.sfAnimation = animation;
      animation.onfinish = () => {
        if (need.sfAnimation !== animation) return;
        need.open = expanded;
        need.sfAnimation = null;
      };
    }
    reducedMotion() { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; }
    observeSlider() {
      if (!this.slider) return;
      this.resizeObserver ||= new ResizeObserver(() => this.updateSlider());
      this.resizeObserver.observe(this.slider);
      this.updateSlider();
    }
    updateSlider() {
      if (!this.slider) return;
      const end = this.slider.scrollWidth - this.slider.clientWidth;
      this.querySelector('[data-sf-slider-step="-1"]').disabled = this.slider.scrollLeft <= 2;
      this.querySelector('[data-sf-slider-step="1"]').disabled = this.slider.scrollLeft >= end - 2;
    }
    revealCategory(button) {
      const pending = this.pendingCategoryReveal;
      if (!pending || pending.button !== button) return;
      this.pendingCategoryReveal = null;
      if (!this.isConnected || !this.mainDetails?.open || !this.mainDetails.classList.contains('menu-opening') || button.getAttribute('aria-expanded') !== 'true') return;
      const viewport = this.closest('.menu-drawer__navigation-container');
      if (!viewport || Math.abs(viewport.scrollTop - pending.scrollTop) > 2) return;
      const category = button.closest('.sf-menu__category').getBoundingClientRect();
      const bounds = viewport.getBoundingClientRect();
      const visual = window.visualViewport;
      const top = Math.max(bounds.top, visual?.offsetTop || 0) + 12;
      // Shopify's preview toolbar overlays the bottom of theme previews only.
      const previewInset = document.getElementById('PBarNextFrame') ? 64 : 0;
      const bottom = Math.min(bounds.bottom, visual ? visual.offsetTop + visual.height : window.innerHeight) - 16 - previewInset;
      if (bottom <= top || category.bottom <= bottom) return;
      // Reveal the bottom, but never move the category heading off-screen when
      // a whole category is taller than the available viewport.
      const delta = Math.min(category.bottom - bottom, Math.max(0, category.top - top));
      const target = Math.min(viewport.scrollHeight - viewport.clientHeight, viewport.scrollTop + delta);
      if (target > viewport.scrollTop + 1) viewport.scrollTo({ top: target, behavior: this.reducedMotion() ? 'auto' : 'smooth' });
    }
    setExpanded(button, expanded, reveal = false) {
      const panel = document.getElementById(button.getAttribute('aria-controls'));
      if (!panel || !this.contains(panel)) return;
      if (this.pendingCategoryReveal?.button === button) this.pendingCategoryReveal = null;
      if (expanded && reveal && panel.querySelector('.sf-menu__children')) {
        const viewport = this.closest('.menu-drawer__navigation-container');
        if (viewport) this.pendingCategoryReveal = { button, scrollTop: viewport.scrollTop };
      }
      const startHeight = panel.hidden ? 0 : panel.getBoundingClientRect().height;
      panel.sfAnimation?.cancel();
      panel.sfAnimation = null;
      if (!expanded) panel.style.removeProperty('min-height');
      panel.hidden = false;
      panel.inert = !expanded;
      button.setAttribute('aria-expanded', String(expanded));
      const categoryTitle = button.closest('.sf-menu__category').querySelector('.sf-menu__title').textContent;
      const label = button.dataset.sfMenuLabel || `Unterkategorien von ${categoryTitle}`;
      button.setAttribute('aria-label', `${label} ${expanded ? 'verbergen' : 'anzeigen'}`);
      if (this.reducedMotion() || !panel.animate) {
        panel.hidden = !expanded;
        if (expanded && reveal) this.revealCategory(button);
        return;
      }
      const endHeight = expanded ? panel.getBoundingClientRect().height : 0;
      const animation = panel.animate([
        { height: `${startHeight}px`, opacity: expanded ? .35 : 1, overflow: 'hidden' },
        { height: `${endHeight}px`, opacity: expanded ? 1 : 0, overflow: 'hidden' }
      ], { duration: 220, easing: 'cubic-bezier(.2,.7,.3,1)' });
      panel.sfAnimation = animation;
      animation.onfinish = () => {
        if (panel.sfAnimation !== animation) return;
        panel.hidden = !expanded;
        panel.sfAnimation = null;
        if (expanded && reveal) this.revealCategory(button);
      };
    }
  });
}

;
/* SF mail CTA: open the existing mobile menu once, only for this quality letter. */
(() => {
  const url = new URL(window.location.href);
  if (url.pathname.replace(/\/$/, '') !== '/collections/beliebteste_produkte' ||
      url.searchParams.get('sf_menu') !== 'qualitaetsbrief_25102026') return;

  // Consume only our switch. Attribution parameters and the fragment stay intact.
  url.searchParams.delete('sf_menu');
  window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
  const mobile = window.matchMedia('(max-width: 989px)');
  if (!mobile.matches || !window.customElements) return;

  let cancelled = false;
  const cancel = () => { cancelled = true; };
  document.addEventListener('pointerdown', cancel, { once: true, capture: true });
  document.addEventListener('keydown', cancel, { once: true, capture: true });
  const cleanup = () => {
    document.removeEventListener('pointerdown', cancel, true);
    document.removeEventListener('keydown', cancel, true);
  };
  const expiry = window.setTimeout(() => { cancelled = true; cleanup(); }, 5000);

  const start = () => {
    window.customElements.whenDefined('header-drawer').then(() => {
      window.requestAnimationFrame(() => window.requestAnimationFrame(() => {
        window.clearTimeout(expiry);
        cleanup();
        if (cancelled || !mobile.matches) return;
        const drawer = document.querySelector('header-drawer #Details-menu-drawer-container');
        const trigger = drawer && drawer.querySelector('summary[aria-controls="menu-drawer"]');
        if (!drawer || drawer.open || !trigger || trigger.getClientRects().length === 0) return;
        const blockingDialog = Array.from(document.querySelectorAll('dialog[open], [aria-modal="true"]'))
          .some(dialog => !drawer.contains(dialog) && !dialog.closest('details:not([open])') &&
            dialog.getClientRects().length > 0 &&
            window.getComputedStyle(dialog).visibility !== 'hidden' &&
            window.getComputedStyle(dialog).opacity !== '0');
        if (blockingDialog) return;
        // Email entry is a landing state, not a manual menu interaction.
        // Keep the native focus trap, but do not paint its initial focus ring.
        if (!document.getElementById('sf-mail-menu-entry-style')) {
          const style = document.createElement('style');
          style.id = 'sf-mail-menu-entry-style';
          style.textContent = `
            @media (max-width: 989px) {
              #Details-menu-drawer-container.sf-mail-menu-quiet-focus > summary,
              #Details-menu-drawer-container.sf-mail-menu-quiet-focus > summary:focus-visible,
              #Details-menu-drawer-container.sf-mail-menu-quiet-focus > summary.focused {
                outline: none !important;
                box-shadow: none !important;
              }
              .section-header:has(#Details-menu-drawer-container.sf-mail-menu-instant[open]) > .header-wrapper,
              #Details-menu-drawer-container.sf-mail-menu-instant[open]:not(.sf-menu-closing) > #menu-drawer,
              #Details-menu-drawer-container.sf-mail-menu-instant[open]:not(.sf-menu-closing) .menu-drawer__navigation-container,
              #Details-menu-drawer-container.sf-mail-menu-instant[open]:not(.sf-menu-closing) sf-visual-menu {
                animation: none !important;
                transition: none !important;
                opacity: 1 !important;
                transform: none !important;
              }
            }
          `;
          document.head.appendChild(style);
        }
        drawer.classList.add('sf-mail-menu-quiet-focus', 'sf-mail-menu-instant');
        const restoreFocusRing = () => {
          drawer.classList.remove('sf-mail-menu-quiet-focus');
          document.removeEventListener('keydown', restoreFocusRing, true);
        };
        const finishEntry = () => {
          if (drawer.open) return;
          restoreFocusRing();
          drawer.classList.remove('sf-mail-menu-instant');
          drawer.removeEventListener('toggle', finishEntry);
        };
        // A keyboard user always gets the ordinary focus indicator back.
        document.addEventListener('keydown', restoreFocusRing, { capture: true });
        drawer.addEventListener('toggle', finishEntry);
        // The theme still handles focus trapping, scroll lock and closing.
        trigger.click();
        if (!drawer.open) finishEntry();
      }));
    }).catch(() => { window.clearTimeout(expiry); cleanup(); });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
